<template>
  <div id="main-container">
    <div class="title"> {{ $t('sectorEditor.title') }} </div>
    <div class="actions">
      <div class="append-menu">
        <v-btn
          id="menu-activator"
          color="primary"
          width="200px"
        >
          {{ $t('sectorEditor.newShape.title') }}
        </v-btn>

        <v-menu activator="#menu-activator">
          <v-list>
            <v-list-item
              v-for="(item, index) in appendMenuItems"
              :key="index"
              :value="index"
              @click="item.action"
              :append-icon="item.img"
            >
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="upload-image">
        <v-btn class="upload-btn" color="#666666" @click="uploadImage"> {{ $t('sectorEditor.uploadImage') }} </v-btn>
        <input
          ref="uploader"
          class="hidden-uploader"
          type="file"
          accept="image/*"
          @change="onFileChanged"
        >
      </div>
      <v-btn class="info-btn" @click="showInfoModal"> {{this.$t('sectorEditor.actionInfoModal.title')}} </v-btn>
    </div>
    <v-card class="editor-section">
      <div ref="container" class="konva-container" :class="canDrawing ? 'currently-drawing' : ''"></div>
      <div class="btn-section">
        <v-btn icon="mdi-minus" @click="zoomWithBtn()"/>
        <v-btn icon="mdi-plus" @click="zoomWithBtn(true)"/>
      </div>
    </v-card>
    <editor-bind-modal type="sector-editor" :title="$t('sectorEditor.editSectorModalComponent.title')" :component="modalComponent" :showModalProp="showEditorModal"/>
    <v-dialog
      transition="dialog-top-transition"
      v-model="isLoading"
      :absolute="true"
      width="auto"
      :content-class="type"
    >
      <v-progress-circular model-value="20" indeterminate></v-progress-circular>
    </v-dialog>
  </div>
</template>

<script>
import { Stage, Layer, Line, Circle, Image, Transformer, Rect} from 'konva';
import Konva from 'konva';
import { toRaw } from 'vue';
import EditorBindModal from './EditorBindModal';
import EditSectorModalComponent from './EditSectorModalComponent';
import { mapActions } from 'vuex';

export default {
  name: 'KonvaSectorEditor',
  components: {
    EditorBindModal,
  },
  props: {
    productLayer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appendMenuItems: [
        {
          title: this.$t('sectorEditor.newShape.circle'),
          img: 'mdi-circle-outline',
          action: () => {
            this.addCircle();
            this.showEditorModal = false;
            if (this.layer.find(`#transformer`).length !== 0) {
              for (const current of this.layer.find(`#transformer`)) {
                current.destroy();
              }
            }
          }
        },
        {
          title: this.$t('sectorEditor.newShape.square'),
          img: 'mdi-square-outline',
          action: () => {
            this.addSquare();
            this.showEditorModal = false;
            if (this.layer.find(`#transformer`).length !== 0) {
              for (const current of this.layer.find(`#transformer`)) {
                current.destroy();
              }
            }
          }
        },
        {
          title: this.$t('sectorEditor.newShape.triangle'),
          img: 'mdi-triangle-outline',
          action: () => {
            this.addTriangle();
            this.showEditorModal = false;
            if (this.layer.find(`#transformer`).length !== 0) {
              for (const current of this.layer.find(`#transformer`)) {
                current.destroy();
              }
            }
          }
        },
        {
          title: this.$t('sectorEditor.newShape.freeShape'),
          img: 'mdi-vector-line',
          action: () => {
            this.canDrawing = true
            this.showEditorModal = false;
            if (this.layer.find(`#transformer`).length !== 0) {
              for (const current of this.layer.find(`#transformer`)) {
                current.destroy();
              }
            }
          }
        },
      ],
      editorSizes: {
        width: undefined,
        height: undefined,
      },
      stage: null,
      layer: null,
      isDrawing: false,
      currentLine: null,
      lines: [],
      nextPointCircle: null,
      initialPoint: null,
      initialPointCircle: null,
      focusedElement: null,
      canDrawing: false,
      showEditorModal: false,
      selectionRectangle: undefined,
      selectionAttrs: {
        x1: undefined,
        y1: undefined,
        x2: undefined,
        y2: undefined,
      },
      selectedShapes: [],
      selectedImage: null,
      isDeletingShape: false,
      sectors: undefined,
      partnerSectors: undefined,
      isLoading: true,
    };
  },
  mounted() {
    this.getLayoutDataApi(this.productLayer.id)
      .then((res) => {
        this.sectors = JSON.parse(res.view_data);
      })
      .catch((err) => {
        console.log(err);
        this.isLoading = false;
      })
    this.getImage();
    this.getPartnerSectors();
    // event from basic modal, we use this for removing shape
    this.emitter.on('okButtonClicked', this.handleOkbutton);

    // we need to set editor sizes for responsibility, and better space usage
    this.setEditorSizes();

    // define stage pace and sizes
    this.stage = new Stage({
      container: this.$refs.container,
      width: this.editorSizes.width,
      height: this.editorSizes.height,
      draggable: true,
      name: 'Stage',
    });
    this.stage.cache();


    document.addEventListener('mousedown', (e) => { 
      if(e.button === 0) this.stage.draggable(false); 
    });
    document.addEventListener('mouseup', (e) => { 
        if(e.button === 0) this.stage.draggable(true); 
    });

    // define layer in stage
    this.layer = new Layer({});
    this.stage.add(this.layer);

    // some event handling for stage
    this.stage.on('click', this.handleClick);
    this.stage.on('touchstart', this.handleClick);
    this.stage.on('wheel', this.zoom);
    this.stage.on('mousedown', this.handleMouseDown);
    this.stage.on('mouseup', this.handleMouseUp);
    this.stage.on('mousemove', this.handleMouseMove);

    // document.addEventListener('keyup', this.handleKeyup);
    
    // define selecton shape, its visible when we start select multiple shape
    this.selectionRectangle = new Rect({
      name: 'Select',
      fill: 'rgba(0,0,255,0.5)',
      visible: false,
    });
    this.layer.add(this.selectionRectangle);

    // layer will be updated with this line
    this.layer.draw();
  },
  beforeUnmount() {
    this.emitter.off('okButtonClicked', this.handleOkbutton);
  },
  methods: {
    ...mapActions({
      addShape: 'siteSettings/addShape',
      setCurrentImage: 'siteSettings/setCurrentImage',
      removeAllShape: 'siteSettings/removeAllShape',
      getPartnerSectorsApi: 'productLayout/getPartnerSectors',
      uploadImageToLayoutApi: 'productLayout/uploadImageToLayout',
      getImageForLayoutApi: 'productLayout/getImageForLayout',
      createRelationSectorToLayoutApi: 'productLayout/createRelationSectorToLayout',
      deleteRelationSectorToLayoutApi: 'productLayout/deleteRelationSectorToLayout',
      updateLayoutApi: 'productLayout/updateLayout',
      getLayoutDataApi: 'productLayout/getLayoutData'
    }),
    showInfoModal() {
      this.emitter.emit('showBasicModal', {
        title: this.$t('sectorEditor.actionInfoModal.title'),
        description: this.$t('sectorEditor.actionInfoModal.description'),
        okButton: this.$t('sectorEditor.actionInfoModal.button'),
        textAlign: 'text-left'
      })
    },
    getPartnerSectors() {
      const payload = {
        sector_layout_id: this.productLayer.id,
      }
      this.getPartnerSectorsApi(payload)
        .then((res) => {
          this.partnerSectors = res;
        })
        .catch((err) => {
          console.log(err);
        })
    },
    loadSectors() {
      for (const current of this.sectors) {
        if (current.points) {
          this.drawShapes(current);
        } else {
          this.drawCircle(current)
        }
      }
      this.isLoading = false;
    },
    drawShapes(current) {
      var scaleX = this.layer.find(`#image`)[0].attrs.width / current.imageSizes.width;
      var scaleY = this.layer.find(`#image`)[0].attrs.height / current.imageSizes.height;

      const line = new Line({
        id: current.id,
        points: current.points.map((point, index) => {
          if (index % 2 === 1) {
            return point * scaleX
          } else {
            return point * scaleY
          }
        }),
        scaleX: current.scaleX,
        scaleY: current.scaleY,
        x: current.x ? current.x * scaleX : null,
        y: current.y ? current.y * scaleY : null,
        strokeWidth: 1,
        closed: true,
        stroke: 'red',
        fill: 'rgba(255, 255, 255, 0.5)',
        name: 'Line',
        draggable: true,
        isLoaded: true,
      });

      this.layer.add(line);
      this.layer.draw();
    },

    // this method load shapes with calculated attributes
    drawCircle(current) {
      var scaleX = this.layer.find(`#image`)[0].attrs.width / current.imageSizes.width;
      var scaleY = this.layer.find(`#image`)[0].attrs.height / current.imageSizes.height;
      const line = new Circle({
        id: current.id,
        radius: current.radius * scaleX,
        x: current.x ? current.x * scaleX : null,
        y: current.y ? current.y * scaleY : null,
        scaleX: current.scaleX,
        scaleY: current.scaleY,
        strokeWidth: 1,
        closed: true,
        stroke: 'red',
        fill: 'rgba(255, 255, 255, 0.5)',
        name: 'Line',
        draggable: true,
        isLoaded: true,
      });

      this.layer.add(line);
      this.layer.draw();
    },
    // this method save shapes to store, just for test and demo
    saveShape(current) {
      let mappedShape;
      if (current.attrs.points) {
        mappedShape = {
          id: current.attrs.id,
          points: current.attrs.points,
          x: current.attrs.x,
          y: current.attrs.y,
          scaleX: current.attrs.scaleX,
          scaleY: current.attrs.scaleY,
          imageSizes: {
            width: this.layer.find(`#image`)[0].attrs.width,
            height: this.layer.find(`#image`)[0].attrs.height,
          },
        };
      } else {
        mappedShape = {
          id: current.attrs.id,
          x: current.attrs.x,
          y: current.attrs.y,
          scaleX: current.attrs.scaleX,
          scaleY: current.attrs.scaleY,
          radius: current.attrs.radius,
          imageSizes: {
            width: this.layer.find(`#image`)[0].attrs.width,
            height: this.layer.find(`#image`)[0].attrs.height,
          },
        };
      }
      return mappedShape;
    },
    saveToApi(list) {
      const payload = {
        body: {
          view_data: JSON.stringify(list),
        },
        layoutId: this.productLayer.id,
      }
      this.updateLayoutApi(payload)
        .then(() => {
          this.$emit('savedLayout');
        })
        .catch((err) => {
          console.log(err);
        })
    },
    async saveShapes() {
      const allShapes = this.layer.find('.Line');
      const allShapesMapped = [];
      if (allShapes.length == 0) {
        this.saveToApi([]);
      }
      for (const current of allShapes) {
        if(!current.attrs.isLoaded && current.attrs.id) {
          const payload = {
            layout_sector_id: this.productLayer.id,
            sector_id: current.attrs.id,
          }
          await this.createRelationSectorToLayoutApi(payload)
            .then(() => {
              current.attrs.isLoaded = true;
              allShapesMapped.push(this.saveShape(current));
            })
            .catch((err) => {
              console.log(err);
            })
        } else {
          if (current.attrs.id) {
            allShapesMapped.push(this.saveShape(current));
          }
        }
      }
      setTimeout(() => {
        this.saveToApi(allShapesMapped);
      }, 100);
    },
    handleOkbutton() {
      if (this.isDeletingShape) {
        if (this.selectedShapes.length != 0) {
          for (const current of this.selectedShapes) {
            if (current.attrs.id) {
              const payload = {
                layoutId: this.productLayer.id,
                sectorId: current.attrs.id,
              }
              this.deleteRelationSectorToLayoutApi(payload)
                .then(() => {
                  current.destroy();
                })
                .catch((err) => {
                  console.log(err);
                })
            } else {
              current.destroy();
            }
          }
        }
        if (this.focusedElement) {
          if (this.focusedElement.attrs.id) {
            const payload = {
              layoutId: this.productLayer.id,
              sectorId: this.focusedElement.attrs.id,
            }
            this.deleteRelationSectorToLayoutApi(payload)
              .then(() => {
                this.removePoly();
              })
              .catch((err) => {
                console.log(err);
              })
          } else {
            this.focusedElement.destroy();
          }
        }
        if (this.layer.find(`#transformer`).length !== 0) {
          for (const current of this.layer.find(`#transformer`)) {
            current.destroy();
          }
        }
        setTimeout(() => {
          this.saveShapes();
        }, 500);
        this.isDeletingShape = false;
        this.showEditorModal = false;
      }
    },
    uploadImage() {
      this.$refs.uploader.click();
    },
    onFileChanged(e) {
      this.selectedImage = e.target.files[0];
      const bodyFormData = new FormData();
      if (this.selectedImage) {
        bodyFormData.append("files", this.selectedImage);
      }
      bodyFormData.append("layoutId", this.productLayer.id);
      this.isLoading = true;
      this.uploadImageToLayoutApi(bodyFormData)
        .then(() => {
          this.getImage();
          this.isLoading = false;
        })
        .catch((err) => {
          console.log(err);
          this.isLoading = false;
        })
    },
    
    getImage() {
      this.getImageForLayoutApi(this.productLayer.id)
        .then((res) => {
          this.setImageBackground(res);
        })
        .catch((err) => {
          console.log(err);
        })
    },

    // this method handling multiple selection, you can select with mouse left button(hold), but its blocked when you push middle or right mouse button
    handleMouseUp(e) {
      if ([2,3].includes(e.evt.which)) {
        return
      }
      if (this.selectionRectangle.visible()) {
        e.evt.preventDefault();
        setTimeout(() => {
          this.selectionRectangle.visible(false);
        });

        let shapes = this.stage.find('.Line');
        let box = this.selectionRectangle.getClientRect();
        let selected = shapes.filter((shape) =>
          Konva.Util.haveIntersection(box, shape.getClientRect())
        );
        this.editMultiplePoly(selected);
        this.selectionRectangle.visible(true);
        this.selectionRectangle.width(0);
        this.selectionRectangle.height(0);
      }
    },

    
    // this method handling multiple selection, you can select with mouse left button(hold), but its blocked when you push middle or right mouse button
    handleMouseDown(e) {
      if ([2,3].includes(e.evt.which)) {
        return
      }
      if (['Image', 'Stage'].includes(e.target.name())) {
        this.selectionAttrs.x1 = this.stage.getRelativePointerPosition().x;
        this.selectionAttrs.y1 = this.stage.getRelativePointerPosition().y;
        this.selectionAttrs.x2 = this.stage.getRelativePointerPosition().x;
        this.selectionAttrs.y2 = this.stage.getRelativePointerPosition().y;
        this.selectionRectangle.zIndex(100);
        this.selectionRectangle.visible(true);
        this.selectionRectangle.width(0);
        this.selectionRectangle.height(0);
      }
    },

    // this method add one circle to layer
    addCircle() {
      this.canDrawing = false;
      const centerX = this.editorSizes.width / 2;
      const centerY = this.editorSizes.height / 2;

      const circle = new Circle({
        x: centerX,
        y: centerY,
        radius: 60,
        stroke: 'red',
        fill: 'rgba(255, 255, 255, 0.5)',
        name: 'Line',
        draggable: true,
        closed: true,
      });

      this.layer.add(circle);
      this.layer.draw();
    },

    // this method add one shape(square) to layer
    addSquare() {
      this.canDrawing = false;
      const centerX = this.editorSizes.width / 2;
      const centerY = this.editorSizes.height / 2;
      const size = 100;

      const square = new Line({
        points: [
          centerX - size / 2,
          centerY - size / 2,
          centerX + size / 2,
          centerY - size / 2,
          centerX + size / 2,
          centerY + size / 2,
          centerX - size / 2,
          centerY + size / 2,
          centerX - size / 2,
          centerY - size / 2
        ],
        closed: true,
        stroke: 'red',
        fill: 'rgba(255, 255, 255, 0.5)',
        name: 'Line',
        draggable: true,
      });

      this.layer.add(square);
      this.layer.draw();
    },

    // this method add one shape(triangle) to layer
    addTriangle() {
      this.canDrawing = false;
      const centerX = this.editorSizes.width / 2;
      const centerY = this.editorSizes.height / 2;
      const size = 100;

      const triangle = new Line({
        points: [
          centerX - size / 2, centerY + (Math.sqrt(3) * size) / 6,
          centerX + size / 2, centerY + (Math.sqrt(3) * size) / 6,
          centerX, centerY - (Math.sqrt(3) * size) / 3
        ],
        closed: true,
        stroke: 'red',
        fill: 'rgba(255, 255, 255, 0.5)',
        name: 'Line',
        draggable: true,
      });

      this.layer.add(triangle);
      this.layer.draw();
    },

    // this method set konva editor size, when page mount
    setEditorSizes() {
      const main = document.getElementById('main-container');
      this.editorSizes.width = main.offsetWidth;
      this.editorSizes.height = main.offsetHeight - 110;
      if (window.innerWidth < 700) {
        this.editorSizes.height = main.offsetHeight - 400;
      }
    },

    // this method track mouse moving, for multiple selection
    handleMouseMove(e) {
      const posX = this.stage.getRelativePointerPosition().x;
      const posY = this.stage.getRelativePointerPosition().y;
      if (this.isDrawing) {
        this.drawNextPointCircle(posX, posY);
        this.updateTempLine(posX, posY);
      }
      if (this.selectionRectangle.visible()) {
        e.evt.preventDefault();
        this.selectionAttrs.x2 = posX;
        this.selectionAttrs.y2 = posY;

        this.selectionRectangle.setAttrs({
          x: Math.min(this.selectionAttrs.x1, this.selectionAttrs.x2),
          y: Math.min(this.selectionAttrs.y1, this.selectionAttrs.y2),
          width: Math.abs(this.selectionAttrs.x2 - this.selectionAttrs.x1),
          height: Math.abs(this.selectionAttrs.y2 - this.selectionAttrs.y1),
        });
      }
    },

    //  this method handling all click event in stage,
    //    if we select drawing option we can draw
    //    if click on shape, we give focus to this shape, and we can modify this shape
    handleClick(event) {
      if ([2,3].includes(event.evt.which)) {
        return
      }
      const posX = this.stage.getRelativePointerPosition().x;
      const posY = this.stage.getRelativePointerPosition().y;
      if (!this.canDrawing && event.target.name() != 'Line') {
        if (!event.target.name().includes('_anchor')) {
          this.showEditorModal = false;
          if (this.layer.find('#transformer').length != 0) {
            this.layer.find('#transformer')[0].destroy();
          }
        }
        return;
      }
      if (!['Line'].includes(event.target.name())) {
        this.initialPointCircle = new Circle({
          x: posX,
          y: posY,
          radius: 5,
          stroke: 'black',
          strokeWidth: 2,
          fillEnabled: false,
          id: 'initialPoint'
        });
        if (!this.isDrawing) {
          this.currentLine = {
            points: [{ x: posX, y: posY }],
            stroke: 'black',
            strokeWidth: 2,
            finished: false,
            closed: false,
            editing: false,
            dragabble: true,
            nodeList: [toRaw(this.initialPointCircle)],
          };
          this.isDrawing = true;
          this.currentLine.points.push({ x: posX, y: posY });
          this.currentLine.nodeList.push(this.nextPointCircle);
          this.lines.push(this.currentLine);
          this.layer.add(this.initialPointCircle);
          this.drawCurves(false);
          this.initialPoint = { x: posX, y: posY };
        } else {
          const posX = this.stage.getRelativePointerPosition().x;
          const posY = this.stage.getRelativePointerPosition().y;

          if (this.isNearInitialPoint(posX, posY)) {
            this.currentLine.points.push({ x: this.initialPoint.x, y: this.initialPoint.y });
            this.isDrawing = false;
            this.initialPoint = null;
            this.initialPointCircle.destroy();
            this.initialPointCircle = null;
            this.currentLine.finished = true;
            this.currentLine.closed = true;
            this.currentLine.editing = true;
            this.drawCurves(true);
            this.removeNextPointCircle();
            this.removeTempLine();
          } else {
            this.currentLine.points.push({ x: posX, y: posY });
            this.drawCurves(false);
          }
        }
        this.removeNextPointCircle();
        this.removeTempLine();
      } else {
        this.focusedElement = event.target;
        this.editPoly(event.target);
      }
    },

    // this method draw final shape, from vector-line components(if we use hand drawing)
    drawCurves(isEnd) {
      for (const curve of this.lines) {
        const line = new Line({
          id: !isEnd ? 'delete' : null,
          points: curve.points.flatMap(point => [point.x, point.y]),
          stroke: 'red',
          strokeWidth: 2,
          closed: curve.closed,
          fill: 'rgba(255, 255, 255, 0.5)',
          draggable: true,
          name: 'Line'
        });
        this.layer.add(line);
        if (this.layer.find(`#delete`).length !== 0 && isEnd) {
          for (const current of this.layer.find(`#delete`)) {
            current.destroy();
            this.lines = [];
            this.currentLine = null;
          }
        }
        if (this.layer.find(`#initialPoint`).length !== 0 && isEnd) {
          for (const current of this.layer.find(`#initialPoint`)) {
            current.destroy();
            this.canDrawing = false;
          }
        }
      }
      this.layer.draw();
    },

    // this method add transformer to shape, than we can edit shape size/position/rotation
    editPoly(poly) {
      if (this.layer.find(`#transformer`).length !== 0) {
        for (const current of this.layer.find(`#transformer`)) {
          current.destroy();
        }
      }
      if (!this.isDrawing) {
        const transformer = new Transformer({
          node: poly,
          rotateEnabled: true,
          rotationSnaps: [0, 90, 180, 270],
          id: 'transformer',
          ignoreStroke: true,
        });
        this.layer.add(transformer);
        this.transformer = transformer;
        setTimeout(() => {
          this.showEditorModal =  true;      
        }, 100);
      }
    },
    
    // this method add transformer to multiple shape, than we can edit this shapes size/position/rotation together
    editMultiplePoly(poly) {
      if (this.layer.find(`#transformer`).length !== 0) {
        for (const current of this.layer.find(`#transformer`)) {
          current.destroy();
        }
      }
      if (!this.isDrawing) {
        setTimeout(() => {
          const multyTransformer = new Transformer({
            nodes: poly,
            rotateEnabled: true,
            rotationSnaps: [0, 90, 180, 270],
            id: 'transformer',
            name: 'multyTransformer',
            ignoreStroke: true,
          });     
          this.multyTransformer = multyTransformer;
          this.layer.add(multyTransformer);
        }, 100);
        this.selectedShapes = poly;
        if (poly.length) {
          setTimeout(() => {
            this.showEditorModal =  true;      
          }, 100);
        }
      }
    },

    // this method add sketch line to stage, we can see next line before click (in hand drawing mode)
    updateTempLine(x, y) {
      this.removeTempLine();
      const lastPoint = this.currentLine.points[this.currentLine.points.length - 1];
      const tempLinePoints = [lastPoint.x, lastPoint.y, x, y];
      this.tempLine = new Line({
        points: tempLinePoints,
        stroke: 'red',
        strokeWidth: 2,
        dash: [5, 5]
      });

      this.layer.add(this.tempLine);
      this.layer.draw();
    },

    // this method remove sketch line
    removeTempLine() {
      if (this.tempLine) {
        this.tempLine.destroy();
        this.tempLine = null;
        this.layer.draw();
      }
    },

    // this method add sketch circle to next line-end
    drawNextPointCircle(x, y) {
      this.removeNextPointCircle();
      this.nextPointCircle = new Circle({
        x,
        y,
        radius: 8,
        stroke: 'red',
        strokeWidth: 2,
        fillEnabled: false
      });
      this.layer.add(this.nextPointCircle);
      this.layer.draw();
    },

    // this method remove sketch circle
    removeNextPointCircle() {
      if (this.nextPointCircle) {
        this.nextPointCircle.destroy();
        this.nextPointCircle = null;
        this.layer.draw();
      }
    },

    // this method remove selected shape
    removePoly() {
      this.focusedElement.destroy();
      this.focusedElement = null;
      if (this.layer.find(`#transformer`).length !== 0) {
        for (const current of this.layer.find(`#transformer`)) {
          current.destroy();
        }
      }
    },
    
    // this method load image to background or add new image if we select upload option
    setImageBackground(imgBase64) {
      const baseImg = new window.Image();
      baseImg.src = `data:image/jpg;base64,${imgBase64}`;
      baseImg.onload = () => {
        this.layer.find('#image')[0]?.destroy();
        const image = new Image({
          image: baseImg,
          x: 0,
          y: 0,
          cornerRadius: 0,
          name: 'Image',
          id: 'image'
        })
        const imagePlaceSizeRatioWidth = this.editorSizes.width / image.width();
        const imagePlaceSizeRatioHeight = this.editorSizes.height / image.height();
        const ratio = imagePlaceSizeRatioHeight < imagePlaceSizeRatioWidth ? imagePlaceSizeRatioHeight : imagePlaceSizeRatioWidth;
        image.height(image.height() * ratio);
        image.width(image.width() * ratio);
        this.layer.add(image);
        if(this.sectors) {
          this.loadSectors();
        }
        this.isLoading = false;
      }
    },

    // this method calculate near points, and if this closer than 5px we use this to finalize shapes
    isNearInitialPoint(x, y) {
      if (!this.initialPoint) {
        return false;
      }
      const dx = Math.abs(this.initialPoint.x - x);
      const dy = Math.abs(this.initialPoint.y - y);
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance <= 5;
    },

    // this method hanlde zoom with mouse middle button
    zoom(e) {
      let scaleBy = 1.2;
      e.evt.preventDefault();
      let oldScale = this.stage.scaleX();
      let pointer = this.stage.getRelativePointerPosition();
      let mousePointTo = {
        x: (pointer.x - this.stage.x()) / oldScale,
        y: (pointer.y - this.stage.y()) / oldScale,
      };
      let direction = e.evt.deltaY > 0 ? -1 : 1;
      if (e.evt.ctrlKey) {
        direction = -direction;
      }
      let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      this.stage.scale({ x: newScale, y: newScale });
      let newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };
      this.stage.position(newPos);
    },

    // this method hanlde zoom with implemented buttons
    zoomWithBtn(isIn = false) {
      let scaleBy = 1.2;
      let oldScale = this.stage.scaleX();

      let center = {
        x: this.stage.width() / 2,
        y: this.stage.height() / 2,
      };

      let relatedTo = {
        x: (center.x - this.stage.x()) / oldScale,
        y: (center.y - this.stage.y()) / oldScale,
      };

      let newScale =
        isIn ? oldScale * scaleBy : oldScale / scaleBy;

      this.stage.scale({
        x: newScale,
        y: newScale
      });

      let newPos = {
        x: center.x - relatedTo.x * newScale,
        y: center.y - relatedTo.y * newScale,
      };
      
      this.stage.position(newPos);
      this.stage.batchDraw();
    },
  },
  computed: {
    // we can modify shape from this modal
    modalComponent() {
      return {
        bind: {
          is: EditSectorModalComponent,
          item: this.focusedElement,
          isMultiple: this.layer ? this.layer.find('.multyTransformer').length != 0 : false,
          partnerSectors: this.partnerSectors,
        },
        on: {
          removeCurrentShape: () => {
            this.isDeletingShape = true;
            this.emitter.emit('showBasicModal', {
              title: this.$t('sectorEditor.removeShapeConfirmationModal.title'),
              description: this.$t('sectorEditor.removeShapeConfirmationModal.description'),
              cancelButton: this.$t('sectorEditor.removeShapeConfirmationModal.cancelButton'),
              okButton: this.$t('sectorEditor.removeShapeConfirmationModal.okButton'),
            })
          },
          saveCurrentShape: (id) => {
            this.focusedElement.attrs.id = id;
            this.saveShapes();
          },
          editSeat: () => {
            this.$emit('editSector', this.focusedElement);
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#main-container {
  height: 80%;
  width: 100%;
  .title {
    margin-top: 16px;
    font-size: 22px;
    font-weight: 550;
  }
  .actions {
    display: flex;
    align-items: center;
    margin: 16px 0;
    .append-menu {
      margin-right: 12px;
    }
    .upload-image {
      .upload-btn {
        color: white;
      }
      .hidden-uploader {
        display: none !important;
      }
    }
    .info-btn {
      margin-left: auto;
    }
    .save-layout {
      margin-left: auto;
    }
  }
  .editor-section {
    position: relative;
    .konva-container {
      &.currently-drawing {
        &:hover {
          cursor: crosshair;
        }
      }
    }
    .btn-section {
      position: absolute;
      bottom: 18px;
      right: 18px;
      width: 110px;
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
